import { Application } from '@/types/application';
import { Detail } from '@/types/detail';
import { Configuration, Metadata } from '@/types/metadata/metadata';
import { ValidationError, ValidationObject } from '@/types/validation';
import { findVariableWithScope } from '@/util/application';
import { arityScope, boundSubroutine } from '@/util/arity';
import { isArray, isEmptyValue, isNullOrUndefined, isObject, isString, isVariable } from '@/util/typeGuards';
import { validateListArity } from './arity';
import { validateDetail } from './blocks';
import { valueUtil } from './value';
import { variableUtil } from './variable';

export const argumentsUtil = (): ValidationObject<Detail[] | undefined> => {
  const validate = (
    application: Application,
    metadata: Metadata,
    linkedSubroutineId: number,
    nodeId: number,
    blockName: string,
    configuration: Configuration,
    details: Detail
  ): ValidationError[] => {
    const scope = arityScope(application, metadata);
    const errors = validateListArity(configuration, details, nodeId, blockName, scope);
    const args = getValue(configuration, details);
    const chosenSubroutine = boundSubroutine(configuration, details, nodeId, scope);
    const subArgs = chosenSubroutine?.args;

    args?.forEach((arg, index) => {
      if (isNullOrUndefined(arg)) {
        errors.push({
          severity: 'error',
          message: `Block ${blockName} is missing required arguments: ${configuration.label}`,
          id: nodeId
        });
        return;
      }

      // A row the interface has no argument for is reported by its arity, not measured against a type.
      const declaredArg = subArgs?.[index];
      const argArgs: [Application, Metadata, number, number, string, Configuration, Detail] = [
        application,
        metadata,
        linkedSubroutineId,
        nodeId,
        blockName,
        configuration,
        arg
      ];
      if (isVariable(arg) && declaredArg) {
        const currentType = findVariableWithScope(arg.name, linkedSubroutineId, arg.scope, application, metadata)?.type;

        if (currentType && declaredArg.type && currentType !== declaredArg.type) {
          errors.push({
            severity: 'error',
            message: `Block ${blockName} requires type: ${declaredArg.type} but variable is of type: ${currentType}`,
            id: nodeId
          });
        }
      } else if (declaredArg && !isEmptyValue(arg)) {
        const argType = declaredArg.type;

        if (
          typeof arg !== argType &&
          !(typeof arg === 'number' && argType === 'integer') &&
          !(typeof arg === 'string' && argType === 'decimal')
        ) {
          errors.push({
            severity: 'error',
            message: `Block ${blockName} requires type: ${argType} but value is of type: ${typeof arg}`,
            id: nodeId
          });
        }
      }

      const argErrors = isVariable(arg)
        ? variableUtil().validate(...argArgs)
        : isString(arg)
          ? valueUtil().validate(...argArgs)
          : validateDetail(application, metadata, linkedSubroutineId, nodeId, blockName, configuration, arg);
      if (argErrors.length) {
        errors.push(...argErrors);
      }
    });

    return errors;
  };

  const getValue = (configuration: Configuration, initVal: unknown): Detail[] | undefined => {
    if (isArray(initVal)) {
      return initVal as Detail[];
    }
    if (isObject(initVal)) {
      const prop = initVal[configuration.value];
      return getValue(configuration, prop);
    }
    return;
  };

  return {
    validate: validate,
    getValue: getValue
  };
};
